import { buildAddendumChanges } from '@/lib/addendumFields'
import type { ServiceAmendContract } from './ServiceDetailsShell'
import type { Addendum, AddendumChange } from '@/lib/schemas/db'
import type { ServiceCollectionName } from '@/db/domain/customerArchive'
import { useOptionalDb } from '@/lib/dbContext'

/** The subset of screen props the amend contract is assembled from. */
export interface ServiceAmendSource {
  addendums?: Addendum[]
  lockedValues?: Record<string, string | undefined>
  amending?: boolean
  onStartAmend?: () => void
  onDiscardAmend?: () => void
  onSaveAddendum?: (
    reason: string,
    changes: AddendumChange[],
  ) => void | Promise<void>
  /** The service's own id and its linked customer. */
  serviceId?: string
  customerId?: string | null
  customerName?: string
  issueSummary?: string
  /** Job equivalents of `issueSummary` and friends; absent on other types. */
  fault?: string
  deviceType?: string
  deviceDescription?: string
  peripherals?: string
  contactAddress?: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  invoiceName?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceAddress?: string
  invoiceVat?: string
  invoiceId?: string
}

/**
 * Assemble the shell's amend contract from a details screen's props plus the
 * live form draft.
 *
 * Returns `undefined` when the screen has no `onSaveAddendum` wired, which is
 * how {@link ServiceDetailsShell} decides between offering an amend flow and
 * falling back to unlock-by-status-change.
 */
export function useServiceAmend(
  props: ServiceAmendSource,
  amendDraft: Record<string, string>,
  /** Which collection the service lives in — needed to relink its customer. */
  collection?: ServiceCollectionName,
): ServiceAmendContract | undefined {
  const db = useOptionalDb()
  const { onSaveAddendum } = props
  if (!onSaveAddendum) return undefined

  const currentValues: Record<string, string | undefined> = {
    customerId: props.customerId ?? '',
    issueSummary: props.issueSummary,
    fault: props.fault,
    deviceType: props.deviceType,
    deviceDescription: props.deviceDescription,
    peripherals: props.peripherals,
    contactAddress: props.contactAddress,
    contactName: props.contactName,
    contactPhone: props.contactPhone,
    contactEmail: props.contactEmail,
    invoiceName: props.invoiceName,
    invoiceEmail: props.invoiceEmail,
    invoicePhone: props.invoicePhone,
    invoiceAddress: props.invoiceAddress,
    invoiceVat: props.invoiceVat,
    invoiceId: props.invoiceId,
  }

  const amending = props.amending ?? false

  return {
    addendums: props.addendums ?? [],
    lockedValues: props.lockedValues ?? {},
    currentValues,
    amending,
    // Only diff while amending: outside that the form mirrors current values,
    // so any difference would be noise.
    pendingChanges: amending
      ? buildAddendumChanges(currentValues, amendDraft)
          // A service can't be unlinked: while the picker is mid-search (no
          // customer chosen) there is no customer change to record yet.
          .filter((change) => change.field !== 'customerId' || amendDraft.customerId)
          .map((change) =>
          // Show names, not ids, for the customer change.
          change.field === 'customerId'
            ? {
                ...change,
                before: props.customerName ?? change.before,
                after: amendDraft.customerName ?? change.after,
              }
            : change,
        )
      : [],
    onStart: () => props.onStartAmend?.(),
    onDiscard: () => props.onDiscardAmend?.(),
    onSave: async (reason, changes) => {
      // A customer change is not a field edit: the server relinks the service
      // (revision + addendum) in one transaction. Everything else is an
      // ordinary addendum.
      const customerChange = changes.find((change) => change.field === 'customerId')
      const rest = changes.filter((change) => change.field !== 'customerId')
      if (customerChange) {
        if (!db || !collection || !props.serviceId || !amendDraft.customerId) {
          throw new Error('Unable to change the customer here.')
        }
        await db.relinkServiceCustomer(
          collection,
          props.serviceId,
          amendDraft.customerId,
          reason,
        )
      }
      if (rest.length > 0) {
        await onSaveAddendum(reason, rest)
      } else {
        props.onDiscardAmend?.()
      }
    },
  }
}
